import { useState, useEffect, useRef, useCallback } from "react";
import { LogIn, LogOut, Coffee, Loader2, Camera, X, MapPin, CheckCircle, AlertCircle, Delete, Lock } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MASTER_EXIT_CODES = ["83528", "4817", "1111"];

const KioskPunchPage = () => {
  // PIN entry
  const [pin, setPin] = useState("");
  const [verifying, setVerifying] = useState(false);

  // Employee session (after PIN entered)
  const [employee, setEmployee] = useState(null);
  const [todayPunches, setTodayPunches] = useState([]);

  // GPS
  const [location, setLocation] = useState(null);
  const [locationError, setLocationError] = useState(null);

  // Camera capture flow
  const [captureAction, setCaptureAction] = useState(null); // "in" | "out" | "lunch_out" | "lunch_in"
  const [cameraReady, setCameraReady] = useState(false);
  const [cameraError, setCameraError] = useState(null);
  const [capturedPhoto, setCapturedPhoto] = useState(null);
  const [submittingPunch, setSubmittingPunch] = useState(false);
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const captureTimerRef = useRef(null);

  // Exit kiosk
  const [showExit, setShowExit] = useState(false);
  const [exitPin, setExitPin] = useState("");

  // iPad lockdown help
  const [showHelp, setShowHelp] = useState(false);

  // Live clock
  const [now, setNow] = useState(new Date());
  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(t);
  }, []);

  // ----- iPad / Kiosk lockdown ------
  useEffect(() => {
    // Block context menu (long-press on iPad shows "Copy / Look Up" menus)
    const onCtx = (e) => { e.preventDefault(); return false; };
    // Block pinch-zoom (multi-touch gesture events on iOS Safari)
    const onGesture = (e) => { e.preventDefault(); };
    // Block accidental drag/select
    const onSelect = (e) => { e.preventDefault(); };
    // Warn if someone tries to navigate away
    const onBeforeUnload = (e) => {
      e.preventDefault();
      e.returnValue = "";
      return "";
    };
    // Block iOS swipe-back gesture from triggering navigation
    const onPopState = () => {
      window.history.pushState(null, "", window.location.pathname);
    };
    // Push a dummy state so first back press is intercepted
    window.history.pushState(null, "", window.location.pathname);

    document.addEventListener("contextmenu", onCtx);
    document.addEventListener("gesturestart", onGesture);
    document.addEventListener("gesturechange", onGesture);
    document.addEventListener("gestureend", onGesture);
    document.addEventListener("selectstart", onSelect);
    document.addEventListener("dragstart", onSelect);
    window.addEventListener("beforeunload", onBeforeUnload);
    window.addEventListener("popstate", onPopState);

    return () => {
      document.removeEventListener("contextmenu", onCtx);
      document.removeEventListener("gesturestart", onGesture);
      document.removeEventListener("gesturechange", onGesture);
      document.removeEventListener("gestureend", onGesture);
      document.removeEventListener("selectstart", onSelect);
      document.removeEventListener("dragstart", onSelect);
      window.removeEventListener("beforeunload", onBeforeUnload);
      window.removeEventListener("popstate", onPopState);
    };
  }, []);

  // Acquire GPS on mount + retry
  useEffect(() => {
    requestLocation();
    const interval = setInterval(() => {
      if (!location) requestLocation();
    }, 30000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const requestLocation = () => {
    if (!navigator.geolocation) {
      setLocationError("Geolocation not supported on this device");
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setLocation({ latitude: pos.coords.latitude, longitude: pos.coords.longitude });
        setLocationError(null);
      },
      (err) => {
        setLocationError(err.message || "Enable location services for the kiosk");
      },
      { enableHighAccuracy: true, timeout: 8000, maximumAge: 30000 }
    );
  };

  // PIN pad helpers
  const appendDigit = (d) => {
    if (pin.length >= 6) return;
    setPin((p) => p + d);
  };
  const backspace = () => setPin((p) => p.slice(0, -1));
  const clearPin = () => setPin("");

  // Submit PIN
  const verifyPin = useCallback(async () => {
    if (!pin || pin.length < 3) return;
    setVerifying(true);
    try {
      const res = await axios.post(`${API}/timeclock/verify-employee`, { code: pin });
      setEmployee(res.data.employee);
      setTodayPunches(res.data.today_punches || []);
      setPin("");
    } catch (err) {
      const msg = err.response?.data?.detail || "Invalid code";
      toast.error(msg);
      setPin("");
    } finally {
      setVerifying(false);
    }
  }, [pin]);

  // Auto-submit when 4+ digits and idle 600ms (typical employee code is 4 digits)
  useEffect(() => {
    if (employee || verifying || pin.length < 4) return;
    const t = setTimeout(() => verifyPin(), 600);
    return () => clearTimeout(t);
  }, [pin, verifyPin, employee, verifying]);

  // Determine next valid action
  const punchTypesToday = todayPunches.map((p) => p.punch_type);
  const lastPunch = todayPunches[todayPunches.length - 1];
  const isPunchedIn = lastPunch && (lastPunch.punch_type === "in" || lastPunch.punch_type === "lunch_in");
  const isOnLunch = lastPunch && lastPunch.punch_type === "lunch_out";

  const canPunchIn = !punchTypesToday.includes("in") || (lastPunch && lastPunch.punch_type === "out");
  const canPunchOut = isPunchedIn;
  const canLunchOut = isPunchedIn && !isOnLunch;
  const canLunchIn = isOnLunch;

  // Camera lifecycle
  const startCamera = async () => {
    setCameraError(null);
    setCapturedPhoto(null);
    setCameraReady(false);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: "user", width: { ideal: 640 }, height: { ideal: 480 } },
        audio: false,
      });
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play();
        setCameraReady(true);
      }
    } catch (err) {
      setCameraError("Camera blocked. Allow camera access in iPad Settings → Safari → Camera.");
    }
  };

  const stopCamera = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
    if (captureTimerRef.current) {
      clearTimeout(captureTimerRef.current);
      captureTimerRef.current = null;
    }
    setCameraReady(false);
    setCapturedPhoto(null);
  };

  // Capture from video to a small JPEG
  const capturePhoto = () => {
    if (!videoRef.current || !cameraReady) return null;
    const video = videoRef.current;
    const canvas = document.createElement("canvas");
    const targetW = 320;
    const ratio = video.videoHeight / video.videoWidth || 0.75;
    canvas.width = targetW;
    canvas.height = Math.round(targetW * ratio);
    const ctx = canvas.getContext("2d");
    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
    const dataUrl = canvas.toDataURL("image/jpeg", 0.7);
    setCapturedPhoto(dataUrl);
    return dataUrl;
  };

  // Auto-capture after camera is ready (1.2s delay so user can position)
  useEffect(() => {
    if (cameraReady && !capturedPhoto) {
      captureTimerRef.current = setTimeout(() => {
        capturePhoto();
      }, 1200);
      return () => clearTimeout(captureTimerRef.current);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cameraReady, capturedPhoto]);

  // After capture, auto-submit punch
  useEffect(() => {
    if (capturedPhoto && captureAction && !submittingPunch) {
      submitPunch(capturedPhoto);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [capturedPhoto, captureAction]);

  const openCameraForPunch = (action) => {
    if (!location) {
      toast.error("Acquiring location… please wait or enable location.");
      requestLocation();
      return;
    }
    setCaptureAction(action);
    startCamera();
  };

  const cancelCapture = () => {
    stopCamera();
    setCaptureAction(null);
  };

  const submitPunch = async (photoDataUrl) => {
    if (!employee || !location || !captureAction) return;
    setSubmittingPunch(true);
    try {
      const res = await axios.post(`${API}/timeclock/punch`, {
        employee_code: employee.code,
        punch_type: captureAction,
        latitude: location.latitude,
        longitude: location.longitude,
        photo_data: photoDataUrl,
      });
      toast.success(res.data.message || "Punch recorded");
      // Refresh punches
      const verRes = await axios.post(`${API}/timeclock/verify-employee`, { code: employee.code });
      setTodayPunches(verRes.data.today_punches || []);
      // Auto-return to PIN screen after 2.5s
      setTimeout(() => {
        stopCamera();
        setCaptureAction(null);
        setEmployee(null);
        setTodayPunches([]);
      }, 2500);
    } catch (err) {
      const msg = err.response?.data?.detail || "Punch failed";
      toast.error(msg, { duration: 5000 });
      stopCamera();
      setCaptureAction(null);
    } finally {
      setSubmittingPunch(false);
    }
  };

  const formatTime = (iso) => {
    if (!iso) return "—";
    try {
      const d = new Date(iso);
      return d.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit", timeZone: "America/Chicago" });
    } catch {
      return "—";
    }
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => stopCamera();
  }, []);

  // Exit kiosk handler
  const handleExitSubmit = () => {
    if (MASTER_EXIT_CODES.includes(exitPin)) {
      window.location.href = "/timecard";
    } else {
      toast.error("Wrong exit code");
      setExitPin("");
    }
  };

  // Add kiosk body class for global lockdown styles (no scroll, no select)
  useEffect(() => {
    document.body.classList.add("kiosk-locked");
    return () => document.body.classList.remove("kiosk-locked");
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-b from-stone-50 to-stone-100 select-none overflow-hidden" style={{ touchAction: "manipulation", WebkitUserSelect: "none", WebkitTouchCallout: "none", WebkitTapHighlightColor: "transparent" }}>
      {/* Header — minimal */}
      <header className="px-6 pt-6 pb-3 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-red-700 flex items-center justify-center">
            <span className="text-white font-bold text-lg">M</span>
          </div>
          <div>
            <div className="text-xs uppercase tracking-widest text-stone-500 font-semibold">Market Place on Oakton</div>
            <div className="text-sm font-bold text-stone-800">PayPunch Kiosk</div>
            <div className="text-[9px] uppercase tracking-widest text-[#A61B1B] font-bold">Powered by AV Tech</div>
          </div>
        </div>
        <div className="text-right">
          <div className="text-2xl font-black text-stone-900 tabular-nums" data-testid="kiosk-clock">
            {now.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit", timeZone: "America/Chicago" })}
          </div>
          <div className="text-xs text-stone-500">
            {now.toLocaleDateString("en-US", { weekday: "long", month: "short", day: "numeric", timeZone: "America/Chicago" })}
          </div>
        </div>
        {/* Tiny lock icon — long-press to reveal exit */}
        <KioskExitTrigger onTrigger={() => setShowExit(true)} onHelp={() => setShowHelp(true)} />
      </header>

      {/* Location indicator */}
      <div className="px-6 pb-2">
        <div className={`inline-flex items-center gap-2 text-xs px-2.5 py-1 rounded-full ${location ? "bg-green-100 text-green-800" : "bg-amber-100 text-amber-800"}`}>
          <MapPin className="w-3 h-3" />
          {location ? "Location verified" : locationError || "Acquiring location…"}
        </div>
      </div>

      <main className="max-w-3xl mx-auto px-6 pb-12">
        {/* PIN ENTRY */}
        {!employee && (
          <div className="bg-white rounded-3xl shadow-xl p-8 mt-4" data-testid="kiosk-pin-card">
            <div className="text-center mb-6">
              <h1 className="text-3xl font-black text-stone-900">Enter Your Code</h1>
              <p className="text-stone-500 mt-1 text-sm">Tap your 4-digit employee code to clock in/out</p>
            </div>

            {/* PIN dots */}
            <div className="flex justify-center gap-3 mb-8" data-testid="kiosk-pin-display">
              {[0, 1, 2, 3, 4, 5].map((i) => (
                <div
                  key={i}
                  className={`w-6 h-6 rounded-full border-2 transition-all ${
                    i < pin.length ? "bg-red-700 border-red-700 scale-110" : "border-stone-300 bg-white"
                  }`}
                />
              ))}
            </div>

            {/* Number pad */}
            <div className="grid grid-cols-3 gap-3 max-w-sm mx-auto">
              {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((n) => (
                <button
                  key={n}
                  onClick={() => appendDigit(String(n))}
                  className="h-20 rounded-2xl bg-stone-100 hover:bg-stone-200 active:bg-stone-300 text-3xl font-bold text-stone-800 transition-colors"
                  data-testid={`kiosk-pin-${n}`}
                >
                  {n}
                </button>
              ))}
              <button
                onClick={clearPin}
                className="h-20 rounded-2xl bg-stone-50 hover:bg-stone-200 text-sm font-semibold text-stone-600 transition-colors"
                data-testid="kiosk-pin-clear"
              >
                Clear
              </button>
              <button
                onClick={() => appendDigit("0")}
                className="h-20 rounded-2xl bg-stone-100 hover:bg-stone-200 active:bg-stone-300 text-3xl font-bold text-stone-800 transition-colors"
                data-testid="kiosk-pin-0"
              >
                0
              </button>
              <button
                onClick={backspace}
                className="h-20 rounded-2xl bg-stone-50 hover:bg-stone-200 flex items-center justify-center text-stone-600 transition-colors"
                data-testid="kiosk-pin-back"
              >
                <Delete className="w-7 h-7" />
              </button>
            </div>

            {verifying && (
              <div className="mt-6 text-center text-stone-500 flex items-center justify-center gap-2">
                <Loader2 className="w-4 h-4 animate-spin" /> Verifying…
              </div>
            )}
          </div>
        )}

        {/* PUNCH ACTIONS */}
        {employee && !captureAction && (
          <div className="bg-white rounded-3xl shadow-xl p-8 mt-4" data-testid="kiosk-punch-card">
            <div className="text-center mb-6">
              <div className="text-xs uppercase tracking-widest text-stone-500 font-semibold">Welcome</div>
              <h1 className="text-4xl font-black text-stone-900 mt-1" data-testid="kiosk-employee-name">{employee.name}</h1>
              <p className="text-stone-500 text-sm mt-1">{employee.department}</p>
            </div>

            {/* Today's punches summary */}
            {todayPunches.length > 0 && (
              <div className="bg-stone-50 rounded-xl p-4 mb-6">
                <div className="text-xs font-semibold text-stone-500 uppercase tracking-wider mb-2">Today</div>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  {todayPunches.map((p, i) => (
                    <div key={i} className="flex items-center gap-2">
                      <span className={`w-2 h-2 rounded-full ${
                        p.punch_type === "in" ? "bg-green-500"
                          : p.punch_type === "out" ? "bg-red-500"
                          : "bg-amber-500"
                      }`} />
                      <span className="text-stone-600 capitalize">{p.punch_type.replace("_", " ")}</span>
                      <span className="font-semibold text-stone-800 ml-auto">{formatTime(p.timestamp)}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Action buttons */}
            <div className="grid grid-cols-2 gap-4">
              <KioskActionBtn
                disabled={!canPunchIn}
                color="green"
                icon={<LogIn className="w-8 h-8" />}
                label="Punch In"
                testid="kiosk-punch-in"
                onClick={() => openCameraForPunch("in")}
              />
              <KioskActionBtn
                disabled={!canPunchOut}
                color="red"
                icon={<LogOut className="w-8 h-8" />}
                label="Punch Out"
                testid="kiosk-punch-out"
                onClick={() => openCameraForPunch("out")}
              />
              <KioskActionBtn
                disabled={!canLunchOut}
                color="amber"
                icon={<Coffee className="w-8 h-8" />}
                label="Lunch Out"
                testid="kiosk-lunch-out"
                onClick={() => openCameraForPunch("lunch_out")}
              />
              <KioskActionBtn
                disabled={!canLunchIn}
                color="amber"
                icon={<Coffee className="w-8 h-8" />}
                label="Lunch In"
                testid="kiosk-lunch-in"
                onClick={() => openCameraForPunch("lunch_in")}
              />
            </div>

            <button
              onClick={() => { setEmployee(null); setTodayPunches([]); setPin(""); }}
              className="w-full mt-6 text-sm text-stone-500 hover:text-stone-800 py-3"
              data-testid="kiosk-cancel"
            >
              ← Cancel / Different Employee
            </button>
          </div>
        )}

        {/* CAMERA CAPTURE OVERLAY */}
        {captureAction && (
          <div className="fixed inset-0 bg-stone-900/95 z-50 flex items-center justify-center p-6" data-testid="kiosk-camera-modal">
            <div className="bg-white rounded-3xl shadow-2xl max-w-md w-full overflow-hidden">
              <div className="p-5 border-b border-stone-200 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Camera className="w-5 h-5 text-red-700" />
                  <div>
                    <div className="font-bold text-stone-900">{employee?.name}</div>
                    <div className="text-xs text-stone-500 uppercase tracking-wider">
                      {captureAction.replace("_", " ")}
                    </div>
                  </div>
                </div>
                {!submittingPunch && (
                  <button onClick={cancelCapture} className="text-stone-400 hover:text-stone-700">
                    <X className="w-5 h-5" />
                  </button>
                )}
              </div>

              <div className="aspect-[4/3] bg-stone-900 relative">
                {cameraError ? (
                  <div className="absolute inset-0 flex items-center justify-center p-6 text-center text-white">
                    <div>
                      <AlertCircle className="w-10 h-10 text-amber-400 mx-auto mb-2" />
                      <p className="text-sm">{cameraError}</p>
                      <button
                        onClick={() => submitPunch(null)}
                        className="mt-4 text-xs text-amber-300 underline"
                      >
                        Submit without photo
                      </button>
                    </div>
                  </div>
                ) : capturedPhoto ? (
                  <img src={capturedPhoto} alt="Captured" className="w-full h-full object-cover" data-testid="kiosk-photo-preview" />
                ) : (
                  <video
                    ref={videoRef}
                    autoPlay
                    playsInline
                    muted
                    className="w-full h-full object-cover"
                  />
                )}
                {!cameraReady && !cameraError && !capturedPhoto && (
                  <div className="absolute inset-0 flex items-center justify-center text-white">
                    <Loader2 className="w-10 h-10 animate-spin" />
                  </div>
                )}
              </div>

              <div className="p-5 text-center">
                {submittingPunch ? (
                  <div className="flex items-center justify-center gap-2 text-stone-700">
                    <Loader2 className="w-5 h-5 animate-spin" /> Recording punch…
                  </div>
                ) : capturedPhoto ? (
                  <div className="flex items-center justify-center gap-2 text-green-700">
                    <CheckCircle className="w-5 h-5" /> Photo captured
                  </div>
                ) : (
                  <p className="text-stone-600 text-sm">Look at the camera — photo will be taken automatically</p>
                )}
              </div>
            </div>
          </div>
        )}

        {/* EXIT KIOSK MODAL */}
        {showExit && (
          <div className="fixed inset-0 bg-stone-900/80 z-50 flex items-center justify-center p-6">
            <div className="bg-white rounded-2xl shadow-xl max-w-sm w-full p-6">
              <div className="flex items-center gap-2 mb-2">
                <Lock className="w-5 h-5 text-red-700" />
                <h3 className="font-bold text-lg text-stone-900">Exit Kiosk</h3>
              </div>
              <p className="text-sm text-stone-500 mb-4">Manager-only. Enter master code to exit kiosk mode.</p>
              <input
                type="password"
                inputMode="numeric"
                value={exitPin}
                onChange={(e) => setExitPin(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && handleExitSubmit()}
                className="w-full px-4 py-3 border-2 border-stone-200 rounded-xl text-lg font-mono focus:border-red-700 focus:outline-none"
                placeholder="Master code"
                autoFocus
                data-testid="kiosk-exit-pin-input"
              />
              <div className="flex gap-2 mt-4">
                <button
                  onClick={() => { setShowExit(false); setExitPin(""); }}
                  className="flex-1 py-3 rounded-xl bg-stone-100 text-stone-700 font-semibold"
                >
                  Cancel
                </button>
                <button
                  onClick={handleExitSubmit}
                  className="flex-1 py-3 rounded-xl bg-red-700 text-white font-semibold"
                  data-testid="kiosk-exit-submit"
                >
                  Exit
                </button>
              </div>
            </div>
          </div>
        )}

        {/* HELP / IPAD LOCKDOWN SETUP MODAL */}
        {showHelp && (
          <div className="fixed inset-0 bg-stone-900/85 z-50 flex items-center justify-center p-4 overflow-y-auto" onClick={() => setShowHelp(false)}>
            <div className="bg-white rounded-2xl shadow-xl max-w-lg w-full p-6 my-8" onClick={(e) => e.stopPropagation()}>
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-bold text-lg text-stone-900 flex items-center gap-2">
                  <Lock className="w-5 h-5 text-red-700" /> Lock the iPad into Kiosk Mode
                </h3>
                <button onClick={() => setShowHelp(false)} className="text-stone-400"><X className="w-5 h-5" /></button>
              </div>
              <div className="space-y-4 text-sm text-stone-700">
                <div>
                  <div className="font-semibold text-stone-900 mb-1">Step 1 — Add this page to Home Screen</div>
                  <ol className="list-decimal ml-5 space-y-1 text-stone-600">
                    <li>In Safari, tap the <b>Share</b> button (square with up arrow)</li>
                    <li>Scroll down and tap <b>"Add to Home Screen"</b></li>
                    <li>Name it "PayPunch" and tap Add</li>
                    <li>Launch the new icon — it opens fullscreen, no Safari bar</li>
                  </ol>
                </div>
                <div>
                  <div className="font-semibold text-stone-900 mb-1">Step 2 — Enable Guided Access (locks the iPad to this app)</div>
                  <ol className="list-decimal ml-5 space-y-1 text-stone-600">
                    <li>iPad Settings → <b>Accessibility</b> → <b>Guided Access</b> → turn <b>On</b></li>
                    <li>Tap <b>Passcode Settings</b> → set a 4-digit code (managers only)</li>
                    <li>Optional: turn on <b>Mirror Display Auto-Lock</b> = Off</li>
                  </ol>
                </div>
                <div>
                  <div className="font-semibold text-stone-900 mb-1">Step 3 — Start the locked session</div>
                  <ol className="list-decimal ml-5 space-y-1 text-stone-600">
                    <li>Open the PayPunch icon</li>
                    <li>Triple-click the <b>Side / Home</b> button</li>
                    <li>Tap <b>Start</b> (top-right)</li>
                  </ol>
                  <p className="mt-2 text-stone-500 text-xs">Now nothing on the iPad works except this kiosk. To exit: triple-click side button and enter your manager passcode.</p>
                </div>
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs text-amber-900">
                  <b>Tip:</b> Also enable <b>Settings → Display → Auto-Lock → Never</b> so the screen never sleeps, and plug the iPad into a charger permanently.
                </div>
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

const KioskActionBtn = ({ disabled, color, icon, label, onClick, testid }) => {
  const colorMap = {
    green: "bg-green-600 hover:bg-green-700 active:bg-green-800",
    red: "bg-red-700 hover:bg-red-800 active:bg-red-900",
    amber: "bg-amber-500 hover:bg-amber-600 active:bg-amber-700",
  };
  return (
    <button
      disabled={disabled}
      onClick={onClick}
      data-testid={testid}
      className={`h-32 rounded-2xl text-white font-bold flex flex-col items-center justify-center gap-2 transition-all ${
        disabled ? "bg-stone-200 text-stone-400 cursor-not-allowed" : `${colorMap[color]} shadow-lg active:scale-95`
      }`}
    >
      {icon}
      <span className="text-lg">{label}</span>
    </button>
  );
};

// Long-press 2-second-hold lock icon to reveal exit modal. Small tap shows help.
const KioskExitTrigger = ({ onTrigger, onHelp }) => {
  const timerRef = useRef(null);
  const heldRef = useRef(false);

  const start = () => {
    heldRef.current = false;
    timerRef.current = setTimeout(() => {
      heldRef.current = true;
      onTrigger();
    }, 2000);
  };
  const cancel = () => {
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = null;
  };
  const handleClick = () => {
    if (!heldRef.current) onHelp();
  };

  return (
    <div className="flex flex-col items-center gap-1">
      <button
        onTouchStart={start}
        onTouchEnd={cancel}
        onTouchCancel={cancel}
        onMouseDown={start}
        onMouseUp={cancel}
        onMouseLeave={cancel}
        onClick={handleClick}
        className="w-8 h-8 rounded-lg flex items-center justify-center text-stone-300 hover:text-stone-600 active:bg-stone-200"
        data-testid="kiosk-lock-btn"
        aria-label="Manager"
      >
        <Lock className="w-4 h-4" />
      </button>
      <div className="text-[9px] text-stone-300 leading-none">hold 2s</div>
    </div>
  );
};

export default KioskPunchPage;
